import React, { ChangeEvent, useMemo, useRef, useState } from 'react';
import { GradientText, GradientTextAction, InputLabel, Spacer, StyledAvatar } from 'src/common';
import Button from 'src/components/form/Button';
import styled from 'styled-components';
import { getProfileCredentials } from 'src/utils/auth';
import MaterialInput from 'src/components/form/MaterialInput';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { PlanTypeSelect, RestrictAccessSelect, WorkspaceTypeSelect } from 'src/components/select';
import { TabWrap } from 'src/components/TabsMenu';
import { useAuth } from 'src/state/auth';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { setCurrencySymbol } from 'src/utils/misc';
import { DailyDigest, DangerZone } from './comps';
import moment from 'moment';
import AutoGrowingInput from 'src/components/form/AutoGrowingInput';
import { useProfileSettingsOptionsQuery, useUpsertWorkspaceMutation, WorkspaceQuery } from 'src/graphql/_generated';
import { useCache } from 'src/graphql/useCache';
import { showSuccessToast, toastMessages } from 'src/components/Toast';

const Div = styled(TabWrap)`
  background-color: ${({ theme }) => theme.colors.white};

  .prof-img {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 12px;
    &__input {
      display: none;
    }
  }

  form {
    .contents {
      display: grid;
      grid-column-gap: 50px;
      grid-row-gap: 20px;
      grid-template-columns: 1fr 1fr;
    }

    .controls {
      display: flex;
      align-items: center;
      justify-content: space-between;
      grid-gap: 13px;
      gap: 13px;
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    form {
      .contents {
        grid-template-columns: 1fr;
        grid-gap: 20px;
      }
    }
  }
`;

interface WorkspaceDetailsProps {
  workspace: NonNullable<WorkspaceQuery['workspace']>;
  loading: boolean;
}

export const WorkspaceDetails: React.FC<WorkspaceDetailsProps> = ({ workspace, loading }) => {
  const [selectedImage, setSelectedImage] = useState<string | undefined>(undefined);
  const { profile, flatOrganisations } = useAuth();
  const { refetchQuery } = useCache();
  const { data: settingsData, loading: settingsLoading } = useProfileSettingsOptionsQuery();

  const validationSchema = useMemo(() => {
    const parentOrgId = workspace.parent?.id;
    const originalName = workspace.name?.trim().toLowerCase() || '';
    const existingWorkspaceNames =
      flatOrganisations
        ?.filter((o) => o.recordType === 'W' && o.parentId === parentOrgId)
        .map((o) => o.name.trim().toLowerCase())
        .filter((name) => name !== originalName) || [];

    return Yup.object({
      name: Yup.string()
        .required('Required')
        .test('unique-name', 'Workspace name already exists', (value) => {
          if (!value) return true;
          return !existingWorkspaceNames.includes(value.trim().toLowerCase());
        }),
      primaryContact: Yup.string().email('Invalid email address').required('Required'),
      isRestricted: Yup.boolean(),
      currency: Yup.string()
        .max(3, 'Currency must be at most 3 characters long')
        .matches(/^[^\s]*$/, 'Currency must not contain spaces')
        .nullable(),
      notificationScheduleTime: Yup.string()
        .test('is-valid-time', 'Invalid time format', (value) => {
          if (!value) return true;
          return moment(value, 'HH:mm', true).isValid();
        })
        .nullable(),
    });
  }, [flatOrganisations, workspace.parent?.id, workspace.name]);

  const inputRef = useRef<HTMLInputElement>(null);

  const [isSaving, setIsSaving] = useState(false);
  const isFormDisabled = loading || settingsLoading || isSaving;

  const [update, { error }] = useUpsertWorkspaceMutation({
    onError: () => setIsSaving(false),
  });

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file) {
      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        setSelectedImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const formik = useFormik({
    validationSchema,
    initialValues: {
      name: workspace.name || '',
      primaryContact: workspace.primaryContact || '',
      isRestricted: workspace.isRestricted,
      plan: workspace.plan || '',
      planType: workspace.planType,
      currency: workspace.currency || '',
      notificationScheduleTime: workspace.notificationScheduleTime,
    },
    onSubmit: async (values) => {
      setIsSaving(true);
      const currency = values.currency;

      if (currency !== workspace.currency) {
        setCurrencySymbol(currency);
        mpEvent(MPEvents.CurrencyChange, {
          currency,
        });
      }

      await update({ variables: { ...values, workspaceId: workspace.id } });
      await refetchQuery({ include: ['workspace', 'flatOrganisationsHome', 'flatOrganisationsNav', 'profile'] });
      showSuccessToast(toastMessages.workspace.updated);
      setIsSaving(false);
    },
    enableReinitialize: true,
  });

  const { values, handleChange, setFieldValue, handleBlur, touched, errors, handleSubmit, isValid, dirty } = formik;

  return (
    <>
      <Div>
        <FormikProvider value={formik}>
          <GradientText>DETAILS</GradientText>
          <Spacer $px={32} />
          <div className="prof-img">
            <StyledAvatar src={selectedImage} $large>
              {getProfileCredentials(workspace.name)}
            </StyledAvatar>
            <GradientTextAction onClick={() => inputRef.current?.click()} px={14} weight={500}>
              Update Photo
            </GradientTextAction>
            <input
              className="prof-img__input"
              ref={inputRef}
              type="file"
              onChange={handleImageChange}
              accept="image/*"
            />
          </div>

          <Spacer $px={32} />
          <form onSubmit={handleSubmit}>
            <div className="contents">
              {/* Row 1 */}
              <div>
                <InputLabel>NAME *</InputLabel>
                <AutoGrowingInput
                  id="name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  fullWidth
                  disabled={isFormDisabled}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
              </div>

              <div>
                <InputLabel>PRIMARY CONTACT EMAIL ADDRESS *</InputLabel>
                <AutoGrowingInput
                  id="primaryContact"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.primaryContact}
                  type="email"
                  fullWidth
                  disabled={isFormDisabled}
                  error={touched.primaryContact && Boolean(errors.primaryContact)}
                  helperText={touched.primaryContact && errors.primaryContact}
                />
              </div>

              {/* Row 2 */}

              <div>
                <InputLabel>ORGANISATION</InputLabel>
                <MaterialInput value={profile?.activeOrganisation?.name} disabled fullWidth />
              </div>

              <div>
                <InputLabel>CURRENCY</InputLabel>
                <MaterialInput
                  id="currency"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.currency}
                  fullWidth
                  disabled={isFormDisabled}
                  error={touched.currency && Boolean(errors.currency)}
                  helperText={touched.currency && errors.currency}
                />
              </div>
            </div>

            <>
              <Spacer $px={40} />
              <GradientText>ACCESS & PLAN</GradientText>
              <Spacer $px={24} />
            </>

            <div className="contents">
              <div>
                <InputLabel
                  tooltip={`<div style="font-size: 12px;padding: 6px 0; max-width: 320px;"><b>Open:</b> (All users within the organisation have automatic access)<br /><br /><b>Restricted:</b> (Users within the organisation must be invited to join the workspace)</div>`}
                >
                  WORKSPACE ACCESS *
                </InputLabel>
                <RestrictAccessSelect
                  value={values.isRestricted}
                  onChange={(val) => {
                    setFieldValue('isRestricted', val.value);
                  }}
                  onBlur={handleBlur('isRestricted')}
                  disabled={isFormDisabled}
                  error={touched.isRestricted && Boolean(errors.isRestricted)}
                  helperText={touched.isRestricted && errors.isRestricted}
                />
              </div>

              <div>
                <InputLabel>ENVIRONMENT TYPE *</InputLabel>
                <WorkspaceTypeSelect
                  restrictedTo={settingsData?.profile?.wsSettingsOptions?.plan || []}
                  value={values.plan}
                  onChange={(val) => setFieldValue('plan', val.value)}
                  onBlur={handleBlur('plan')}
                  disabled={isFormDisabled}
                  error={touched.plan && Boolean(errors.plan)}
                  helperText={touched.plan && errors.plan}
                />
              </div>

              <div>
                <InputLabel>PRICING PLAN</InputLabel>
                <PlanTypeSelect
                  restrictedTo={settingsData?.profile?.wsSettingsOptions?.planType || []}
                  value={values.planType}
                  onChange={(val) => setFieldValue('planType', val.value)}
                  onBlur={handleBlur('planType')}
                  disabled={isFormDisabled}
                  error={touched.planType && Boolean(errors.planType)}
                  helperText={touched.planType && errors.planType}
                />
              </div>
            </div>

            <>
              <Spacer $px={40} />
              <GradientText>DAILY DIGEST</GradientText>
              <Spacer $px={24} />
            </>

            <div className="contents">
              <DailyDigest workspaceId={workspace.id} members={workspace.users} />
            </div>

            {error && (
              <>
                <Spacer $px={20} />
                <div className="err">{error.message || 'Something went wrong'}</div>
              </>
            )}
            <Spacer $px={50} />
            <div className="controls">
              {workspace.actionsOnThisWorkspace?.canEditSettings && (
                <Button
                  primary
                  type="submit"
                  disabled={!isValid || isSaving || !dirty}
                  loading={isSaving}
                  $constWidth={220}
                  onClick={() =>
                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Update',
                      tags: ['WORKSPACE'],
                    })
                  }
                >
                  UPDATE
                </Button>
              )}
              <DangerZone
                workspace={workspace}
                refetch={() =>
                  refetchQuery({ include: ['workspace', 'flatOrganisationsHome', 'flatOrganisationsNav', 'profile'] })
                }
              />
            </div>
          </form>
        </FormikProvider>
      </Div>
    </>
  );
};
